import { t } from "@lingui/macro";
import { useResponsiveProp } from "@shopify/restyle";
import { add, format, parse } from "date-fns";
import React, { useEffect, useState } from "react";
import { Platform, useWindowDimensions, Dimensions } from "react-native";
import { useMediaQuery } from "react-responsive";

import {
  CalendarIcon,
  CloseIcon,
} from "@whoosh/design-system/src/components/icons_v2/general";
import {
  Box,
  Pressable,
  Center,
  VStack,
  Text,
  HStack,
} from "@whoosh/design-system/src/components/primitive";
import { NumericalDayOfWeek } from "@whoosh/utilities/src/calendars";

import { Menu } from "../../../overlay/Menu";
import { checkWhichQuickDateResponseIsSelected } from "../utils/checkWhichQuickDateResponseIsSelected";
import { getQuickDateResponse } from "../utils/getQuickDateResponse";
import { QuickDateRangeOption } from "./QuickDateRangeOption";
import { TwoCalendarMonths } from "./TwoCalendarMonths";

type QuickDateRangePickerProps = {
  startDate: string;
  endDate: string;
  onChange: (args: { startDate: string; endDate: string }) => void;
  clubWeekStartsOn: NumericalDayOfWeek;
};

export const QuickDateRangePicker = ({
  startDate,
  endDate,
  onChange,
  clubWeekStartsOn,
}: QuickDateRangePickerProps) => {
  const initialQuickSelection = checkWhichQuickDateResponseIsSelected(
    startDate,
    endDate
  );

  const isMobileLayout = useResponsiveProp({
    base: 1,
    md: 0,
  });

  const { width } = useWindowDimensions();

  const [isOpen, setIsOpen] = useState(false);

  const [isCustomRangeOpen, setIsCustomRangeOpen] = useState(
    initialQuickSelection === "Custom"
  );

  useEffect(() => {
    setIsCustomRangeOpen(initialQuickSelection === "Custom");
  }, [initialQuickSelection, isOpen]);

  const nowObj = new Date();
  const nextMonth = add(nowObj, { months: 1 });
  const nextMonthLabel = format(nextMonth, "MMMM");
  const secondMonth = add(nowObj, { months: 2 });
  const secondNextMonthLabel = format(secondMonth, "MMMM");

  const curQuickDateResponse = checkWhichQuickDateResponseIsSelected(
    startDate,
    endDate
  );

  const shadow = {
    shadowColor: "black" as const,
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 4,
  };

  const currentValueString =
    curQuickDateResponse === "Custom"
      ? `${format(
          parse(startDate, "yyyy-MM-dd", new Date()),
          "MMM do"
        )} to ${format(parse(endDate, "yyyy-MM-dd", new Date()), "MMM do")}`
      : curQuickDateResponse === "Next30"
      ? t`Next 30 days`
      : curQuickDateResponse === "NextMonth"
      ? nextMonthLabel
      : curQuickDateResponse === "SecondNextMonth"
      ? secondNextMonthLabel
      : curQuickDateResponse === "Previous30"
      ? t`Previous 30 days`
      : curQuickDateResponse === "FullYear"
      ? t`Full Year`
      : curQuickDateResponse;

  const handleUpdateRange = (startDate: string, endDate: string) => {
    onChange({ startDate, endDate });
    setIsCustomRangeOpen(false);
    setIsOpen(false);
  };

  const isTwoCalendarWidth = useMediaQuery({ query: "(min-width: 1150px)" });

  const windowWidth = Dimensions.get("window").width;
  const isActionSheetMode = windowWidth < 501;

  const showTwoCalendars = isTwoCalendarWidth && Platform.OS === "web";
  const areRangeOptionsHidden = isCustomRangeOpen && isMobileLayout;
  const shouldHaveShadow = isActionSheetMode
    ? false
    : isCustomRangeOpen
    ? !areRangeOptionsHidden
      ? false
      : true
    : false;

  return (
    <Menu
      openPosition="bottom-start"
      overrideIsOpen={isOpen}
      onClose={() => {
        setIsOpen(false);
      }}
      trigger={(triggerProps) => {
        return (
          <Pressable
            accessibilityHint={t`quick-date-range-picker`}
            aria-label={t`quick-date-range-picker`}
            testID="quick-date-range-picker"
            backgroundColor="text-inverse"
            {...triggerProps}
            onPress={() => {
              setIsOpen(!isOpen);
            }}
          >
            <HStack
              borderColor="surface-dividers"
              style={{
                alignItems: "center",
                borderWidth: 1.5,
                borderRadius: 8,
                paddingHorizontal: 16,
                paddingVertical: 14,
                height: 50,
              }}
            >
              <Text style={{ flex: 1 }}>{currentValueString}</Text>
              <Center marginLeft={3}>
                <CalendarIcon />
              </Center>
            </HStack>
          </Pressable>
        );
      }}
      content={() => (
        <Box
          borderRadius="4"
          borderWidth={isActionSheetMode ? 0 : 1}
          borderColor="surface-dividers"
          {...(shouldHaveShadow && shadow)}
        >
          <Box
            bg="transparent"
            minWidth={
              Platform.OS !== "web"
                ? showTwoCalendars
                  ? 800
                  : isMobileLayout
                  ? width - 48
                  : 550
                : "auto"
            }
          >
            <HStack
              borderRadius="4"
              overflow="hidden"
              py={6}
              paddingRight={isMobileLayout ? 3 : 6}
              width={isCustomRangeOpen ? "auto" : 150}
              backgroundColor={"text-inverse"}
            >
              <VStack
                style={{
                  display: areRangeOptionsHidden ? "none" : "flex",
                }}
                flexGrow={0}
                width={
                  isCustomRangeOpen && areRangeOptionsHidden && isMobileLayout
                    ? 0
                    : 150
                }
              >
                <QuickDateRangeOption
                  initialQuickSelection={initialQuickSelection}
                  quickSelectionOption={"Next30"}
                  isCustomRangeOpen={isCustomRangeOpen}
                  label={t`Next 30 days`}
                  onPress={() => {
                    onChange(getQuickDateResponse("Next30"));
                    setIsOpen(false);
                  }}
                />
                <QuickDateRangeOption
                  initialQuickSelection={initialQuickSelection}
                  quickSelectionOption={"Previous30"}
                  isCustomRangeOpen={isCustomRangeOpen}
                  label={t`Previous 30 days`}
                  onPress={() => {
                    onChange(getQuickDateResponse("Previous30"));
                    setIsOpen(false);
                  }}
                />
                <QuickDateRangeOption
                  initialQuickSelection={initialQuickSelection}
                  quickSelectionOption={"NextMonth"}
                  isCustomRangeOpen={isCustomRangeOpen}
                  label={nextMonthLabel}
                  onPress={() => {
                    onChange(getQuickDateResponse("NextMonth"));
                    setIsOpen(false);
                  }}
                />
                <QuickDateRangeOption
                  initialQuickSelection={initialQuickSelection}
                  quickSelectionOption={"SecondNextMonth"}
                  isCustomRangeOpen={isCustomRangeOpen}
                  label={secondNextMonthLabel}
                  onPress={() => {
                    onChange(getQuickDateResponse("SecondNextMonth"));
                    setIsOpen(false);
                  }}
                />
                <QuickDateRangeOption
                  initialQuickSelection={initialQuickSelection}
                  quickSelectionOption={"FullYear"}
                  isCustomRangeOpen={isCustomRangeOpen}
                  label={t`Full Year`}
                  onPress={() => {
                    onChange(getQuickDateResponse("FullYear"));
                    setIsOpen(false);
                  }}
                />
                <QuickDateRangeOption
                  initialQuickSelection={initialQuickSelection}
                  quickSelectionOption={"Custom"}
                  isCustomRangeOpen={isCustomRangeOpen}
                  label={t`Custom Range`}
                  onPress={() => {
                    setIsCustomRangeOpen(true);
                  }}
                />
              </VStack>
              <Box
                style={{
                  display: isCustomRangeOpen ? "flex" : "none",
                }}
                flex={isCustomRangeOpen ? 1 : 0}
                width={isCustomRangeOpen ? "auto" : 0}
                height={isCustomRangeOpen ? "auto" : 0}
                paddingLeft={isCustomRangeOpen ? (isMobileLayout ? 3 : 8) : 0}
                overflow="hidden"
              >
                {isMobileLayout ? (
                  <Pressable
                    onPress={() => {
                      setIsCustomRangeOpen(false);
                    }}
                  >
                    <CloseIcon />
                  </Pressable>
                ) : null}
                <TwoCalendarMonths
                  curStartDate={startDate}
                  curEndDate={endDate}
                  handleUpdateRange={handleUpdateRange}
                  clubWeekStartsOn={clubWeekStartsOn}
                />
              </Box>
            </HStack>
          </Box>
        </Box>
      )}
    />
  );
};
